/**
 * 显示"不知道"的统一写法。
 *
 * 台面上有三种不同的空,混在一起就是骗人:
 *   ① 没有数据源(hub 还没有这张表)   → NA,配文案说明第几期接
 *   ② 有源但此刻为空(一台设备都没注册) → 空态提示
 *   ③ 读失败                          → 报错,**绝不**降级成 ①②
 * 这里只管 ①:把 null 画成 '—',顺带保证没人对 null 做算术。
 */
export const NA = '—';

export const num = (v: number | null | undefined, digits = 0): string =>
  v == null || Number.isNaN(v) ? NA : v.toFixed(digits);

export const pct = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v) ? NA : `${Math.round(v)}%`;

export const degC = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v) ? NA : `${v.toFixed(0)}°C`;

export const text = (v: string | null | undefined): string =>
  v == null || v === '' ? NA : v;

/** 进度条宽度:不知道就画 0 宽(而不是把 null 当 0 显示成"电量 0%")。 */
export const barWidth = (v: number | null | undefined): string =>
  v == null || Number.isNaN(v) ? '0%' : `${Math.max(0, Math.min(100, v))}%`;

/**
 * RFC3339 / ISO 字符串 → 「x分钟前」;不知道就 NA(不是"刚刚")。
 *
 * **只收字符串,不收裸数字。** hub 两边的时间单位不一样:设备注册表是 unix 秒,
 * 台账是**毫秒**(ledger.nowMs)。一个同时收 number 的 timeAgo 迟早会把毫秒当秒,
 * 算出"55000年前"而没人发现 —— 单位靠函数名区分,不靠调用点记得住。
 */
export function timeAgo(ts: string | null | undefined): string {
  if (ts == null || ts === '') return NA;
  const ms = new Date(ts).getTime();
  if (Number.isNaN(ms)) return NA;
  const mins = Math.floor((Date.now() - ms) / 60000);
  if (mins < 1) return '刚刚';
  if (mins < 60) return `${mins}分钟前`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}小时前`;
  return `${Math.floor(hours / 24)}天前`;
}

/** unix **秒** → ISO 字符串;0/缺席 → null(**不是** 1970)。设备注册表用这个。 */
export const isoFromUnixSec = (sec: number | null | undefined): string | null =>
  sec == null || sec === 0 ? null : new Date(sec * 1000).toISOString();

/** unix **毫秒** → ISO 字符串。台账(ledger.nowMs)用这个。 */
export const isoFromUnixMs = (ms: number | null | undefined): string | null =>
  ms == null || ms === 0 ? null : new Date(ms).toISOString();

/** unix 秒 → 「x分钟前」。wg 对端的 lastSeen 是秒。 */
export const timeAgoSec = (sec: number | null | undefined): string =>
  sec == null || sec === 0 ? NA : timeAgo(new Date(sec * 1000).toISOString());
